import { useEffect, useState } from 'react';
import { ITypedBaseResponse } from '@nkr/models/base-response';
import { IOpoSaveResult } from '@nkr/models/opo-document';
import { IOpoPackagingData, IOpoPackagingRequest } from '@nkr/models/opo-packaging';
import { AppActionCreators } from '@nkr/store/application';
import { AuthActionCreators } from '@nkr/store/authorization';
import { useAppHookContext } from '@nkr/utils/rest-api';
import usePortalOpo from './use-portal-opo';

interface IRetVal {
    data: IOpoPackagingData | null,
    loading: boolean,
    error: string,
    save: (request: IOpoPackagingRequest) => Promise<IOpoSaveResult>
}

const useOpoPackaging = (): IRetVal => {
    const { dispatch } = useAppHookContext();
    const { opoId } = usePortalOpo();
    const [data, setData] = useState<IOpoPackagingData | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [refreshKey, setRefreshKey] = useState<number>(0);

    const url = `${__API__}/opo/${opoId}/packaging`;

    useEffect(() => {
        if (!opoId) {
            return;
        }

        let isCurrent = true;

        const fetchData = async () => {
            setLoading(true);
            setError('');

            await dispatch(AppActionCreators.showLoadingAsync('Getting packaging settings, one moment please...'));

            try {
                const response: ITypedBaseResponse<IOpoPackagingData> = await dispatch(AuthActionCreators.apiGet({ url }));

                if (!isCurrent) {
                    return;
                }

                if (response && response.data.success) {
                    setData(response.data.results);
                } else {
                    setError('Unable to load packaging settings');
                }
            } catch (err) {
                if (isCurrent) {
                    setError('Unable to load packaging settings');
                }
            } finally {
                if (isCurrent) {
                    setLoading(false);
                    dispatch(AppActionCreators.hideLoading());
                }
            }
        };

        fetchData();

        return () => {
            isCurrent = false;
            dispatch(AppActionCreators.hideLoading());
        };
    }, [opoId, refreshKey]);

    const save = async (request: IOpoPackagingRequest): Promise<IOpoSaveResult> => {
        await dispatch(AppActionCreators.showLoadingAsync('Saving packaging settings, one moment please...'));

        try {
            const response: ITypedBaseResponse<any> = await dispatch(AuthActionCreators.apiPut({ url, data: request }));

            if (response && response.data.success) {
                setRefreshKey(x => x + 1);
                return { success: true, message: '' };
            }

            return { success: false, message: (response && response.data.message) || 'Unable to save packaging settings' };
        } catch (err) {
            return { success: false, message: 'Unable to save packaging settings' };
        } finally {
            dispatch(AppActionCreators.hideLoading());
        }
    };

    return { data, loading, error, save };
}

export default useOpoPackaging;
